import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import _ from 'lodash';
import { Employee } from '../models/employee';
import { AuxiliarService } from './auxiliar.service';
import { Tenant } from '../models/tenant';
import { EmployeeDrawerComponent } from '../components/drawers/employee-drawer/employee-drawer.component';
import { TranslateService } from '@ngx-translate/core';
import { TenantService } from './tenant.service';
import { Doc } from '../models/document';
import { DocDrawerComponent } from '../components/drawers/doc-drawer/doc-drawer.component';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {

  constructor(
    private httpClient: HttpClient,
    private auxiliarService: AuxiliarService,
    private translate: TranslateService,
    private tenantService: TenantService,
  ) { }

  // [GET] employees
  public getEmployees(tenant: Tenant): Observable<{ [index: string]: any; }> {
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + environment.api_endpoints.employees,
        { observe: 'response', headers }
      );
  }

  // [GET] employees for schedule
  public getScheduleEmployees(tenant: Tenant): Observable<{ [index: string]: any; }> {
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + environment.api_endpoints.employeesSchedule,
        { observe: 'response', headers }
      );
  }

  // [GET] employees documents
  public getEmployeesDocuments(tenant: Tenant): Observable<{ [index: string]: any; }> {
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.employees}/all/documents`,
        { observe: 'response', headers }
      );
  }

  // [POST] create employee
  public createEmployee(employee: Employee): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.post<HttpResponse<object>>
      (
        environment.api_server + environment.api_endpoints.employees,
        employee, { observe: 'response', headers }
      );
  }

  // [PATCH] update employee
  public updateEmployee(employee: Employee): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.patch<HttpResponse<object>>
      (
        environment.api_server + `${environment.api_endpoints.employees}/${employee.id}`,
        employee, { observe: 'response', headers }
      );
  }

  // [DELETE] delete employee
  public deleteEmployee(employee: Employee, tenant: Tenant): Observable<{ [index: string]: any; }> {
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.delete<HttpResponse<object>>(
      environment.api_server + `${environment.api_endpoints.employees}/${employee.id}`,
      { observe: 'response', headers }
    );
  }

  // [GET] available employees & occupancy
  public getAvailableEmployees(): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get<HttpResponse<object>>
      (
        environment.api_server + environment.api_endpoints.employeesOccupancy,
        { observe: 'response', headers }
      );
  }

  // [PATCH] change other empoyee's password
  public changeEmployeePassword(employee: Employee, password: string): Observable<object> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.patch<HttpResponse<object>>
      (environment.api_server + `${_.replace(environment.api_endpoints.passwordChangeEmployee, ':id', employee.id)}`,
        { password }, { params: {}, withCredentials: false, observe: 'response', headers }
      );
  }

  // [POST] upload employee document
  public uploadEmployeeDoc(employee: Employee, docForm: Doc, tenant?: Tenant): Observable<{ [index: string]: any; }> {
    const docTenant = tenant || this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': docTenant.id });

    return from(this.auxiliarService.generateMD5(docForm.document)).pipe(
      switchMap((md5sum: string) => {
        const formData = new FormData();
        formData.append('document', docForm.document);
        formData.append('md5sum', md5sum);
        formData.append('name', docForm.name);
        formData.append('description', docForm.description);

        return this.httpClient.post<HttpResponse<object>>(
          environment.api_server + `${environment.api_endpoints.auths}/${employee.id}/document`,
          formData, { observe: 'response', headers }
        );
      })
    );
  }

  // [DELETE] delete employee document
  public deleteEmployeeDoc(employee: Employee, document: Doc): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.delete<HttpResponse<object>>(
      environment.api_server + `${environment.api_endpoints.auths}/${employee.id}/document/${document.id}`,
      { observe: 'response', headers }
    );
  }
  
  // [GET] download employee doc
  public downloadEmployeeDoc(employee: Employee, doc: Doc): Observable<HttpResponse<Blob>> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get(environment.api_server + `${environment.api_endpoints.auths}/${employee.id}/document/${doc.id}`,
      { observe: 'response', headers, responseType: 'blob' });
  }

  // open employee new doc drawer
  public openDocDetail(employee: Employee, tenant?: Tenant): Promise<any> {
    return this.auxiliarService.openDrawer(DocDrawerComponent, { 
      data: { employee, tenant },
      position: 'right',
      iconRight: 'pi pi-file',
      titleRight: this.translate.instant('button.new-doc'),
      width: '600px'
    });
  }

  // [GET] employee avatar
  public getEmployeeAvatar(employeeId: string): Observable<HttpResponse<Blob>> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get(
      environment.api_server + `${environment.api_endpoints.employees}/${employeeId}/avatar`, {
        observe: 'response', headers, responseType: 'blob'
      }
    );
  }

  // [POST] upload employee avatar
  public uploadEmployeeAvatar(employee: Employee, blob: any): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });

    return from(this.auxiliarService.generateMD5(blob)).pipe(
      switchMap((md5sum: string) => {
        const formData = new FormData();
        formData.append('document', blob);
        formData.append('md5sum', md5sum);
        formData.append('name', employee.person?.givenname);

        return this.httpClient.post<HttpResponse<object>>(
          environment.api_server + `${environment.api_endpoints.employees}/${employee.id}/avatar`,
          formData, { observe: 'response', headers }
        );
      })
    );
  }

  // [GET] check employee username
  public checkEmployeeUsername(username: string): Observable<{ [index: string]: any; }> {
    const tenant = this.tenantService.getAppTenant();
    const headers = new HttpHeaders({ 'X-Tenant': tenant.id });
    return this.httpClient.get(
      environment.api_server + `${environment.api_endpoints.auths}/username/${username}`, 
      { observe: 'response', headers }
    );
  }

  // open employee detail
  public async openEmployeeDetail(employee: Employee | undefined, tenant: Tenant): Promise<boolean> {
    const res = await this.auxiliarService.openDrawer(EmployeeDrawerComponent, { 
      employee,
      tenant,
      position: 'right',
      iconRight: 'pi pi-user',
      titleRight: employee ? this.translate.instant('button.edit-employee') : this.translate.instant('button.new-employee'),
      width: '700px'
    });
    return res;
  }
}
